import { Button, Form, Modal, notification } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getEventById, updateEventById, getEvents } from "store/slices/eventsSlice";
import { closeModal } from "store/slices/modalsSlice";
import EventForm from "./event-form";
import { buildImageUrl } from "utils/imageUrl";
import { plainTextToHtml } from "utils/htmlContent";

const EditEventModal = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector((state) => state.modals);
  const { event, loading } = useSelector((state) => state.events);

  const open = isModalOpen && modalType === "edit_event";

  // Load event data when modal opens
  useEffect(() => {
    if (open && modalProps?.eventId) {
      dispatch(getEventById(modalProps.eventId));
    }
  }, [dispatch, modalProps?.eventId, open]);

  // Set form fields when event data is loaded
  useEffect(() => {
    if (event && open) {
      const imageFile = event.image
        ? [
            {
              uid: "-1",
              name: event.image?.originalname || "image.png",
              status: "done",
              url: buildImageUrl(event.image),
            },
          ]
        : [];

      // Reset form first to clear any previous data
      form.resetFields();
      
      // Then set the new values
      form.setFieldsValue({
        postType: event.postType,
        title: event.title || "",
        excerpt: event.excerpt || "",
        // Les anciens posts sont stockés en texte brut : on les convertit
        // en HTML pour que l'éditeur riche garde les sauts de ligne
        content: plainTextToHtml(event.content || ""),
        studentName: event.studentName || "",
        testimonial: plainTextToHtml(event.testimonial || ""),
        videoUrl: event.videoUrl || "",
        description: plainTextToHtml(event.description || ""),
        format: event.type || event.format || "online",
        location: event.location || "",
        registrationLink: event.registrationLink || "",
        eventDate: event.eventDate ? dayjs(event.eventDate) : null,
        eventTime: event.eventTime ? dayjs(event.eventTime, "HH:mm") : null,
        publishMode: "scheduled",
        publishDate: event.publishedAt ? dayjs(event.publishedAt) : null,
        publishTime: event.publishedAt ? dayjs(event.publishedAt) : null,
        image: imageFile,
      });
    }
  }, [event, open, form]);

  const handleCancel = () => {
    form.resetFields();
    dispatch(closeModal());
  };

  const onSubmit = async (values) => {
    const formData = new FormData();

    // Image - only append if new image uploaded
    if (values?.image?.length > 0 && values.image[0].originFileObj) {
      formData.append("image", values.image[0].originFileObj);
    }

    formData.append("title", values.title);
    formData.append("postType", values.postType);

    if (values.postType === "article") {
      formData.append("excerpt", values.excerpt || "");
      formData.append("content", values.content || "");
    }

    if (values.postType === "testimonial") {
      formData.append("studentName", values.studentName || "");
      formData.append("testimonial", values.testimonial || "");
      if (values.videoUrl) formData.append("videoUrl", values.videoUrl);
    }

    if (values.postType === "video") {
      formData.append("videoUrl", values.videoUrl || "");
    }

    if (values.postType === "event") {
      formData.append("description", values.description || "");
      formData.append("type", values.format || "online");
      if (values.format === "onsite" && values.location) {
        formData.append("location", values.location);
      }
      if (values.eventDate) {
        formData.append("eventDate", dayjs(values.eventDate).format("YYYY-MM-DD"));
      }
      if (values.eventTime) {
        formData.append("eventTime", dayjs(values.eventTime).format("HH:mm"));
      }
      if (values.registrationLink) {
        formData.append("registrationLink", values.registrationLink);
      }
    }

    const publishMode = values.publishMode || "scheduled";

    if (publishMode === "scheduled" && values.publishDate && values.publishTime) {
      const publishDateTime = dayjs(values.publishDate)
        .hour(dayjs(values.publishTime).hour())
        .minute(dayjs(values.publishTime).minute())
        .second(0)
        .millisecond(0);
      formData.append("publishedAt", publishDateTime.toISOString());
    } else {
      formData.append("publishedAt", dayjs().toISOString());
    }

    try {
      const result = await dispatch(updateEventById({ id: modalProps.eventId, data: formData })).unwrap();
      
      // Refresh the events list
      await dispatch(getEvents());
      
      notification.success({ 
        message: t("events.crud.edit.success"),
        description: "L'événement a été modifié avec succès"
      });
      
      handleCancel();
    } catch (error) {
      console.error("Update error:", error);
      notification.error({
        message: t("events.crud.edit.error"),
        description: error?.message || error || "Une erreur est survenue",
      });
    }
  };

  return (
    <Modal
      title={t("events.crud.edit.title")}
      open={open}
      onCancel={handleCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("common.cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
          {t("common.save")}
        </Button>,
      ]}
      destroyOnClose
    >
      {open && <EventForm form={form} onSubmit={onSubmit} key={modalProps?.eventId} />}
    </Modal>
  );
};

export default EditEventModal;
